import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";

import useSearchParamsHandler from "./useSearchParamsHandler";
import {
  allPagesAtom,
  allProductsAtom,
  currentPageAtom,
  fetchProductsAtom,
  productPerPageAtom,
  productsInfoAtom,
  productsLoadingStatusAtom,
} from "../atom/products";
import { useAtom } from "jotai";
import { allCategoryInfoAtom } from "../atom/calibers";
import { categoriesPageModeAtom } from "../atom/listings";

const useUpdateProducts = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { params } = useSearchParamsHandler();
  const [, fetchProducts] = useAtom(fetchProductsAtom);
  const [productsLoadingStatus, setProductsLoadingStatus] = useAtom(
    productsLoadingStatusAtom,
  );
  const [productsInfo] = useAtom(productsInfoAtom);
  const [currentPage] = useAtom(currentPageAtom);
  const [allPages] = useAtom(allPagesAtom);
  const [productPerPage] = useAtom(productPerPageAtom);
  const [allProducts] = useAtom(allProductsAtom);
  const [allCalibers] = useAtom(allCategoryInfoAtom);
  const [categoriesPageMode] = useAtom(categoriesPageModeAtom);

  const lastCaliberRef = useRef(null);
  const cachedDataRef = useRef(null);
  const lastCaliberQueryRef = useRef(null);

  const removeWcFromSearchParams = useCallback((params) => {
    if (params) {
      params.delete("wc");
    }
    return params;
  }, []);

  const settingProducts = useCallback(
    async (pathname, search, state) => {
      try {
        const combinedParams = new URLSearchParams(
          search.startsWith("?") ? search.substring(1) : search,
        );

        if (state && typeof state === "string") {
          const stateQueryParamsString = state.split("?")[1] || "";
          const stateParams = new URLSearchParams(stateQueryParamsString);
          for (const [key, value] of stateParams.entries()) {
            if (!combinedParams.has(key)) {
              combinedParams.set(key, value);
            }
          }
        }

        if (!combinedParams.has("page")) combinedParams.set("page", "1");
        if (!combinedParams.has("sort_by"))
          combinedParams.set("sort_by", "landed_cpr");

        const currentCaliberEncoded = combinedParams.get("caliber");
        let decodedCaliberForRef = null;

        if (currentCaliberEncoded) {
          decodedCaliberForRef = decodeURIComponent(
            currentCaliberEncoded,
          ).replace(/\+/g, " ");
          lastCaliberRef.current = decodedCaliberForRef;
        } else {
          const pathSegments = pathname.split("/").filter(Boolean);
          const caliberFromPath = pathSegments[1] || null;

          if (caliberFromPath && allCalibers.length > 0) {
            const matchedCaliber = allCalibers
              .flatMap((item) => item.calibers)
              .find(
                (caliberItem) => caliberItem.seo_caliber === caliberFromPath,
              );

            if (matchedCaliber) {
              decodedCaliberForRef = matchedCaliber.caliber;
              lastCaliberRef.current = decodedCaliberForRef;

              combinedParams.set("caliber", matchedCaliber.cal_id);
            } else {
              return;
            }
          } else {
            return;
          }
        }

        const pathnameSegments = pathname.split("/").filter(Boolean);
        const isCategoryPage = pathnameSegments.length === 1;

        const finalApiParams = removeWcFromSearchParams(combinedParams);
        const finalApiQueryString = finalApiParams.toString();

        if (
          !finalApiQueryString &&
          !isCategoryPage &&
          !pathname.includes("product")
        ) {
          return;
        }

        if (
          lastCaliberRef.current === lastCaliberQueryRef.current &&
          cachedDataRef.current &&
          !searchParams.has("page") &&
          !finalApiParams.has("page") &&
          !location.search.includes("page=")
        ) {
          return;
        }

        setProductsLoadingStatus("loading");
        const userZip = Cookies.get("user-zip");
        const response = await fetchProducts(
          `${finalApiQueryString}${userZip ? `&user-zip=${userZip}` : ""}`,
        );

        if (response) {
          cachedDataRef.current = response;
          lastCaliberQueryRef.current = lastCaliberRef.current;
        }
      } catch (e) {
        console.error(e);
        setProductsLoadingStatus("idle");
      }
    },
    [
      fetchProducts,
      setProductsLoadingStatus,
      removeWcFromSearchParams,
      lastCaliberRef,
      allCalibers,
      searchParams,
      location.search,
    ],
  );

  useEffect(() => {
    const currentPageParam = searchParams.get("page");
    if (currentPageParam && currentPageParam !== currentPage.toString()) {
      setSearchParams({ ...params, page: currentPageParam }, { replace: true });
    }
  }, [currentPage, searchParams, params, setSearchParams]);

  useEffect(() => {
    if (!searchParams.has("page")) {
      setSearchParams({ ...params, page: "1" }, { replace: true });
    }
  }, [searchParams, params, setSearchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
    settingProducts(location.pathname, location.search, location.state);
  }, [
    location.pathname,
    location.search,
    location.state,
    settingProducts,
    allCalibers,
    categoriesPageMode,
  ]);

  const data = useMemo(
    () => ({
      currentPage,
      allPages,
      productsInfo,
      productPerPage,
      allProducts,
      productsLoadingStatus,
    }),
    [
      currentPage,
      allPages,
      productsInfo,
      productPerPage,
      allProducts,
      productsLoadingStatus,
    ],
  );

  return data;
};

export default useUpdateProducts;
